import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { 
  LayoutDashboard,
  Users,
  Phone,
  Target,
  Calendar,
  Settings,
  LogOut,
  ChevronDown,
  ChevronRight,
  BarChart3,
  Megaphone,
  GitMerge,
  ClipboardList,
  Shield,
  Building2,
  Handshake,
  Package,
  CheckSquare,
  Activity,
  PieChart,
  Crown,
  Sparkles,
  X
} from 'lucide-react';

const Sidebar = ({ onClose }) => {
  const { profile, signOut } = useAuth();
  const { isDark } = useTheme();
  const location = useLocation();
  const [expandedCategories, setExpandedCategories] = useState(['TABLEAU DE BORD']);

  const toggleCategory = (categoryName) => {
    setExpandedCategories(prev =>
      prev.includes(categoryName)
        ? prev.filter(c => c !== categoryName)
        : [...prev, categoryName]
    );
  };

  const menuItems = [
    {
      name: 'TABLEAU DE BORD',
      icon: LayoutDashboard,
      gradient: 'from-blue-500 to-indigo-500',
      items: [
        { name: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' }
      ]
    },
    {
      name: 'COMMERCIAL',
      icon: Target,
      gradient: 'from-purple-500 to-pink-500',
      items: [
        { name: 'Leads', icon: Users, path: '/leads' },
        { name: 'Campagnes', icon: Megaphone, path: '/campaigns' },
        { name: 'Appels', icon: Phone, path: '/calls' }
      ]
    },
    {
      name: 'VENTES',
      icon: GitMerge,
      gradient: 'from-emerald-500 to-teal-500',
      items: [
        { name: 'Deals', icon: GitMerge, path: '/deals' },
        { name: 'Partenaires', icon: Handshake, path: '/partners' },
        { name: 'Factures', icon: Package, path: '/invoices' }
      ]
    },
    {
      name: 'ANALYTIQUES',
      icon: BarChart3,
      gradient: 'from-amber-500 to-orange-500',
      items: [
        { name: 'Analytiques', icon: Activity, path: '/analytics' },
        { name: 'Rapports', icon: PieChart, path: '/reports' },
        { name: 'Calendrier', icon: Calendar, path: '/calendar' }
      ]
    },
    {
      name: 'ADMIN',
      icon: Shield,
      gradient: 'from-red-500 to-rose-500',
      items: [
        { name: 'Utilisateurs', icon: Users, path: '/users' },
        { name: 'Rôles', icon: Shield, path: '/roles' },
        { name: 'Paramètres', icon: Settings, path: '/settings' }
      ]
    }
  ];

  return (
    <aside className={`
      w-64 h-full flex flex-col
      transition-colors duration-300
      ${isDark 
        ? 'bg-gray-900/95 border-r border-gray-800' 
        : 'bg-white/95 border-r border-gray-200'
      }
      backdrop-blur-xl
    `}>
      {/* Close button - mobile */}
      <button
        onClick={onClose}
        className="lg:hidden absolute top-4 right-4 p-2 rounded-lg bg-gray-100 dark:bg-gray-800"
      >
        <X size={20} />
      </button>

      {/* Logo */}
      <div className="p-6">
        <div className="flex items-center gap-3">
          <div className="relative">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-purple-600 flex items-center justify-center shadow-lg">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-500 rounded-full border-2 border-white dark:border-gray-900"></div>
          </div>
          <div>
            <h1 className={`text-lg font-bold bg-gradient-to-r from-primary-500 to-purple-600 bg-clip-text text-transparent`}>
              Insight360
            </h1>
            <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              CRM Professionnel
            </p>
          </div>
        </div>
      </div>

      {/* User profile */}
      <div className={`
        mx-4 p-4 rounded-xl mb-4
        ${isDark ? 'bg-gray-800/50' : 'bg-gray-50'}
      `}>
        <div className="flex items-center gap-3">
          <div className="relative">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary-500 to-purple-600 flex items-center justify-center text-white font-bold">
              {profile?.full_name?.[0] || profile?.email?.[0]?.toUpperCase()}
            </div>
            <div className="absolute -bottom-1 -right-1 w-3 h-3 bg-emerald-500 rounded-full border-2 border-white dark:border-gray-900"></div>
          </div>
          <div className="flex-1 min-w-0">
            <p className={`text-sm font-medium truncate ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
              {profile?.full_name || profile?.email}
            </p>
            <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              {profile?.role || 'User'}
            </p>
          </div>
          <Crown className="w-4 h-4 text-yellow-500" />
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-4 pb-4 scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600">
        {menuItems.map((category) => {
          const isExpanded = expandedCategories.includes(category.name);
          const Icon = category.icon;

          return (
            <div key={category.name} className="mb-2">
              <button
                onClick={() => toggleCategory(category.name)}
                className={`
                  w-full flex items-center justify-between p-3 rounded-xl
                  transition-all duration-200 group
                  ${isExpanded 
                    ? isDark ? 'bg-gray-800' : 'bg-gray-100'
                    : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                  }
                `}
              >
                <div className="flex items-center gap-3">
                  <div className={`
                    w-8 h-8 rounded-lg bg-gradient-to-br ${category.gradient}
                    flex items-center justify-center
                    transition-transform duration-200 group-hover:scale-110
                  `}>
                    <Icon className="w-4 h-4 text-white" />
                  </div>
                  <span className={`text-sm font-medium ${
                    isDark ? 'text-gray-200' : 'text-gray-700'
                  }`}>
                    {category.name}
                  </span>
                </div>
                {isExpanded ? (
                  <ChevronDown className={`w-4 h-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                ) : (
                  <ChevronRight className={`w-4 h-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                )}
              </button>

              {isExpanded && (
                <div className="ml-11 mt-1 space-y-1">
                  {category.items.map((item) => {
                    const isActive = location.pathname === item.path;

                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => onClose?.()}
                        className={`
                          flex items-center gap-3 px-3 py-2 rounded-lg
                          transition-all duration-200 group
                          ${isActive
                            ? `bg-gradient-to-r ${category.gradient} text-white shadow-lg`
                            : isDark
                              ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-800'
                              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                          }
                        `}
                      >
                        <item.icon className="w-4 h-4" />
                        <span className="text-sm">{item.name}</span>
                        {item.badge && (
                          <span className="ml-auto px-1.5 py-0.5 text-xs bg-red-500 text-white rounded-full">
                            {item.badge}
                          </span>
                        )}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Logout button */}
      <div className="p-4 border-t border-gray-200 dark:border-gray-800">
        <button
          onClick={signOut}
          className={`
            w-full flex items-center gap-3 px-3 py-2 rounded-lg
            transition-all duration-200
            ${isDark
              ? 'text-gray-400 hover:text-red-400 hover:bg-red-500/10'
              : 'text-gray-600 hover:text-red-600 hover:bg-red-50'
            }
          `}
        >
          <LogOut className="w-4 h-4" />
          <span className="text-sm">Déconnexion</span>
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;


